import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { API_HANDLERS } from 'api/apiHandlers';
import { useDeleteFileMutation } from 'api/mutations/useDeleteFileMutation';
import { useUploadAttachment } from 'api/mutations/useUploadAttachment';
import { useVideoUploadAttachment } from 'api/mutations/useVideoUploadAttachment';
import { alert } from 'components/Alerts';
import { IMAGE_MIME_TYPES, VIDEO_MIME_TYPES } from 'components/MediaUploadZone/constants';
import { Attachment, FormDocumentsKeyValue } from 'types';
import { prepareFilesBeforeUpload } from 'utils';
interface UseFormAttachmentsProps {
  attachedImages: Attachment[];
  attachedDocs: Attachment[];
  attachedVideos: Attachment[];
  isEdit?: boolean;
  currentPostId?: string;
  setFormValue: (key: FormDocumentsKeyValue['key'], value: FormDocumentsKeyValue['value']) => void;
}

export function useFormAttachments({
  attachedImages,
  attachedDocs,
  attachedVideos,
  setFormValue,
  isEdit,
  currentPostId,
}: UseFormAttachmentsProps) {
  const { uploadAttachmentMutation, isLoading: isLoadingFileUpload } = useUploadAttachment();
  const { uploadVideoAttachmentMutation, isVideoLoading } = useVideoUploadAttachment();
  const { deleteFileMutation } = useDeleteFileMutation();

  const attachmentsCount = attachedImages.length + attachedDocs.length + attachedVideos.length;
  const [progress, setProgress] = useState(0);
  console.log(setProgress, 'setProgress');
  const { t } = useTranslation();

  async function handleUploadMutation(file: File, category_ids: string[], post_id: string) {
    const data = new FormData();
    data.append('file', file);
    const response = await uploadAttachmentMutation({ file: data, category_ids, post_id });
    return response.data;
  }

  async function handleUploadVideoMutation(file: File, category_ids: string[], post_id: string) {
    const data = new FormData();
    data.append('video', file);
    const response = await uploadVideoAttachmentMutation({ file: data, category_ids, post_id });
    return response.data;
  }

  async function handleAddImages(files: FileList, category_ids: string[] = [], post_id = '') {
    const filesToAdd = prepareFilesBeforeUpload(attachmentsCount, files);

    if (filesToAdd) {
      filesToAdd.forEach(async (file) => {
        const newImage = await handleUploadMutation(file, category_ids, post_id);

        if (newImage) {
          attachedImages.push(newImage);

          setFormValue('images', attachedImages);
        }
      });
    }
  }

  async function handleAddVideos(files: FileList, category_ids: string[] = [], post_id = '') {
    const filesToAdd = prepareFilesBeforeUpload(attachmentsCount, files);

    if (filesToAdd) {
      filesToAdd.forEach(async (file) => {
        const newVideo = await handleUploadVideoMutation(file, category_ids, post_id);

        if (newVideo) {
          attachedVideos.push(newVideo);

          setFormValue('videos', attachedVideos);
        }
      });
    }
  }

  async function handleAddDocuments(files: FileList, category_ids: string[] = [], post_id = '') {
    const filesToAdd = prepareFilesBeforeUpload(attachmentsCount, files);

    if (filesToAdd) {
      const INVALID_CHARACTERS = /[!@#$%&*()\[\]{}\\/?<>|:;"'~]/;

      filesToAdd.forEach(async (file) => {
        if (INVALID_CHARACTERS.test(file.name)) {
          alert.error(t('toasts.fileUpload.error'));
          return;
        }

        const newDoc = await handleUploadMutation(file, category_ids, post_id);

        if (newDoc) {
          attachedDocs.push(newDoc);
          setFormValue('docs', attachedDocs);
        }
      });
    }
  }

  async function handleDeleteAttachment(attachmentId: string, type: 'images' | 'docs' | 'videos') {
    const allAttachments = [...attachedImages, ...attachedDocs, ...attachedVideos];
    const attachment = allAttachments.find((a) => a.id === attachmentId);
    const isNewlyUploaded = Boolean(attachment?.created_at);

    if (isEdit) {
      const usageResponse = await API_HANDLERS.FILES.GET_USAGE(attachmentId);
      const { usage, linked_posts } = usageResponse.data;
      if (usage.posts === 1 && linked_posts[0]?.id === currentPostId) {
        const force = true;
        await deleteFileMutation({ id: attachmentId, force });
      }
    } else if (isNewlyUploaded) {
      await deleteFileMutation({ id: attachmentId });
    }

    if (type === 'images') {
      setFormValue(
        'images',
        attachedImages.filter((image) => image.id !== attachmentId),
      );
    }

    if (type === 'docs') {
      setFormValue(
        'docs',
        attachedDocs.filter((document) => document.id !== attachmentId),
      );
    }

    if (type === 'videos') {
      setFormValue(
        'videos',
        attachedVideos.filter((video) => video.id !== attachmentId),
      );
    }
  }

  const toFileList = (arr: File[]): FileList => {
    const dt = new DataTransfer();
    arr.forEach((f) => dt.items.add(f));
    return dt.files;
  };

  async function handleUploadAll(files: FileList, post_id = '') {
    const imageFiles = Array.from(files).filter((f) => IMAGE_MIME_TYPES.includes(f.type));
    const videoFiles = Array.from(files).filter((f) => VIDEO_MIME_TYPES.includes(f.type));
    const docFiles = Array.from(files).filter(
      (f) => !IMAGE_MIME_TYPES.includes(f.type) && !VIDEO_MIME_TYPES.includes(f.type),
    );

    if (imageFiles.length) await handleAddImages(toFileList(imageFiles), [], post_id);
    if (videoFiles.length) await handleAddVideos(toFileList(videoFiles));
    if (docFiles.length) await handleAddDocuments(toFileList(docFiles), [], post_id);
  }

  return {
    handleAddImages,
    handleAddDocuments,
    handleAddVideos,
    handleUploadAll,
    handleDeleteAttachment,
    progress,
    isLoadingFileUpload: isLoadingFileUpload || isVideoLoading,
  };
}
